import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  BannerContainerVm,
  BannerVm,
  ContainerVm,
  ResourceVm,
  StorefrontVm,
  TabVm,
} from 'qp-ui-interface';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ClevertapEvent } from 'src/app/analytics/clevertap/enum/clevertap.eventType';
import { ClevertapService } from 'src/app/analytics/clevertap/service/clevertap.service';
import { AppEventName } from 'src/app/analytics/newrelic/enums/EventName';
import { LanguageService } from 'src/app/language.service';
import { Tags } from 'src/app/shared/common/interfaces/display-info';
import { LoginStatusType } from 'src/app/shared/interfaces/account.interfaces';
import { LocalStorageKeys } from 'src/app/shared/LocalStorageKeys';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { Destroyable } from 'src/app/shared/services/destroyable';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { UserEntitlementService } from 'src/app/shared/services/evergent/entitlement/user-entitlement.service';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';
import { SessionStorageService } from 'src/app/shared/services/storage/session-storage.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import {
  ContentDetailInfo,
  ContentDetailService,
} from 'src/app/shared/shared-components/content-detail/content-detail.service';
import { PlayerErrorComponent } from 'src/app/shared/shared-components/player-prompts/player-error-dialog/player-error.component';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { directToPlayer } from 'src/app/shared/utils/playerUtils';
import { ErrorFactory } from '../../shared/modules/errors/handler/ErrorFactory';
import { ConfigService } from '../../shared/services/config.service';
import { NavigationService } from '../../shared/services/navigation.service';
import { NavigationLibraryService } from '../../shared/services/navigation/navigation.service';
import { TabService } from '../../shared/services/tabs/tab.service';
import {
  PlayerEventType,
  SimplePlayerService,
} from '../../shared/shared-components/simple-player/simple-player.service';
import { CatalogService } from '../catalog.service';
import { StorefrontHttpService } from '../storefront-http.service';
import { NewrelicService } from './../../analytics/newrelic/services/newrelic.service';
import { DataService } from './../../data.service';
import { IN_APP_BANNER_ID } from 'src/app/shared/common/const/common.const';
import { UserSubscriptionStatus } from 'src/app/shared/enums/subscription';
import { TizenService } from 'src/app/shared/services/tizen/tizen.service';

@Component({
  selector: 'app-storefront',
  templateUrl: './storefront.component.html',
  styleUrls: ['./storefront.component.scss'],
})
export class StorefrontComponent
  extends Destroyable
  implements OnInit, OnDestroy
{
  public static classState = 'StorefrontComponent';
  @ViewChild('legacyCarouselTrack', { static: false })
  legacyCarouselTrack: ElementRef;
  @ViewChild('legacyCarouselWrapper', { static: false })
  legacyCarouselWrapper: ElementRef;
  @Input() lang = 'en';
  selectedTabId!: string;
  containers: ContainerVm[];
  isLoading = true;
  tempContainers: ContainerVm[] = [];
  isHeroFocused = true;
  continueWatchingContainer: ContainerVm;
  continueWatchingIndex: number | null = null;
  currentResource: ResourceVm;
  contentDetailInfo: ContentDetailInfo;
  saved = false;
  continueWatchingResetId: string | null = null;
  isHeroImageLoaded = false;
  inAppBannerData: ContainerVm;
  canFocus: boolean = false;

  // Carousel Properties
  public carouselCurrentIndex = 0;
  public scrollDirection: 'left' | 'right' = 'right';
  private isAnimating = false;
  private animationDuration = 300;
  private scrollTimeout: any;

  // Constants
  VIEW_ALL_INDEX = 20;

  private readonly pageSize = 3;
  private pageNumber = 1;
  private maxContainers = 50;
  private loadingContainers = true;
  private autoScrollInterval: any;
  private isComponentVisible = false;
  private autoScrollSubscription: any; // Track auto-scroll subscription
  private currentVerticalIndex: number = 0;
  public carouselResources: ResourceVm[] | undefined;
  public isLegacyChromium: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private storefrontHttpService: StorefrontHttpService,
    private navigationService: NavigationService,
    private sessionStorage: SessionStorageService,
    private navService: NavigationLibraryService,
    private readonly tabService: TabService,
    private newrelic: NewrelicService,
    private readonly config: ConfigService,
    private catalogService: CatalogService,
    private contentDetailService: ContentDetailService,
    private simplePlayerService: SimplePlayerService,
    private toast: ToastService,
    private localStorage: LocalStorageService,
    private userEntitlementService: UserEntitlementService,
    private clevertapService: ClevertapService,
    private readonly authService: AuthService,
    private readonly dialogService: DialogService,
    private readonly languageService: LanguageService,
    private readonly tizenService: TizenService
  ) {
    super();
  }

  get titleImage(): string {
    return (
      this.currentResource?.heroTitleImage ||
      this.currentResource?.customTitleImage
    );
  }

  customTagFontSize(tag: Tags): string {
    return tag.key === null && this.isHeroFocused
      ? 'fontSize.21'
      : 'fontSize.18';
  }

  ngOnInit(): void {
    this.dataService.isFromDeeplink = false;
    this.setupComponentState();
    this.setupSubscriptions();
    this.setupAutoScroll();
    // Subscribe to route changes to handle new data
    if (!this.sessionStorage.getItem(KEYS.TIME_TO_LOAD_APP)) {
      this.sessionStorage.setItem(
        KEYS.TIME_TO_LOAD_APP,
        '' + new Date().getTime()
      );
    }

    // Clevertap Login and Login Event
    if (this.dataService.isFromLinkTv) {
      this.dataService.isFromLinkTv = false;
      const contactID = this.sessionStorage.getItem(KEYS.MAIN_CONTACT_ID);
      const currentProfileInfo = JSON.parse(
        this.sessionStorage.getItem(KEYS.MAIN_CONTACT)
      );
      const mainUser = JSON.parse(
        this.sessionStorage.getItem(KEYS.MAIN_CONTACT)
      );
      this.clevertapService.loginUser(
        contactID,
        `${currentProfileInfo[0]?.firstName} ${currentProfileInfo[0]?.lastName}`,
        mainUser?.email,
        mainUser?.mobileNumber
      );
      this.clevertapService.addEvent(ClevertapEvent.LOGIN, {
        isNewUser: false,
        loginStatus: LoginStatusType.LOGGED,
        otpCalls: undefined,
        source: 'TV',
      });
      this.newrelic.sendAppEvent(AppEventName.LOGIN, {});
    }

    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: Params) => {
        this.selectedTabId = params?.id;
        this.dataService.activeTab = params?.id;

        // Read resolver data
        const {
          containers,
          totalContainers,
          personalisationData,
          isCollection,
        } = this.route.snapshot.data?.storefrontData;

        const filterCarouselContainer =
          containers !== undefined
            ? containers.find((container) => container?.layout === 'carousel')
            : undefined;
        if (!filterCarouselContainer) {
          this.currentResource = containers[0]?.resources[0];
          this.navService.focusContainer(
            'horizontal-carousel-cards-0',
            true,
            0
          );
        }
        if (this.localStorage.getItem(KEYS.FRESH_LAUNCH) === 'true') {
          this.localStorage.removeItem(KEYS.FRESH_LAUNCH);
        }

        this.dataService.currentResourceContainerName = '';
        this.dataService.currentResourceContainerId = '';
        this.dataService.currentResourcePositionInTray = undefined;
        this.navigationService.searchItemPosition = undefined;
        this.reset();

        // Update continue watching container after pagination
        if (personalisationData?.resources?.length > 0) {
          this.continueWatchingContainer = personalisationData;
        }

        if (containers?.length > 0 && totalContainers !== undefined) {
          this.maxContainers = totalContainers;
          this.setCarousels(totalContainers, [].concat(containers));
        } else {
          this.fetchStorefrontData(this.pageNumber, this.pageSize)
            .then((result) => {
              const { containers, totalContainers } = result;
              this.setCarousels(totalContainers, containers);
            })
            .catch(() => {
              this.dialogService.openDialog(PlayerErrorComponent, {
                data: {
                  errorCode: '11223344',
                },
              });
            });
        }
      });
    this.navService.focusContainer('carousel-track', true, 0);
    if (this.dataService.showLinkTvSuccessToast) {
      this.toast.success('link_tv_success');
      this.dataService.showLinkTvSuccessToast = false;
    }

    this.sendNewRelicStart();
  }

  private resetDestroySubject(): void {
    if ((this as any).destroy$) {
      this.cleanup();
    }
    (this as any).destroy$ = new Subject<void>();
  }

  private setupSubscriptions(): void {
    // Component visibility subscription
    this.dataService.currentComponent
      .pipe(takeUntil(this.destroy$))
      .subscribe((component) => {
        this.isComponentVisible = component === StorefrontComponent;
        if (!this.isComponentVisible) {
          this.clearAutoScrollTimer();
        }
      });

    // Current resource subscription
    this.dataService.currentResource
      .pipe(takeUntil(this.destroy$))
      .subscribe((resource) => {
        if (resource) {
          if (this.currentResource?.id !== resource?.id) {
            const heroTitleImage = document.getElementById(
              'heroTitleImage'
            ) as HTMLImageElement;
            if (heroTitleImage) {
              heroTitleImage.src = '';
            }
            this.isHeroImageLoaded = false;
            this.currentResource = resource;
          }
          if (this.isHeroFocused) {
            this.currentResource.heroTitleImage =
              this.contentDetailService.getOptimizedImageUrl(
                this.currentResource.heroTitleImage
              );
            this.contentDetailInfo =
              this.contentDetailService.getContentDetailInfo(
                this.currentResource,
                this.isHeroFocused
              );
          } else {
            this.currentResource.customTitleImage =
              this.contentDetailService.getOptimizedImageUrl(
                this.currentResource.customTitleImage
              );
            this.contentDetailInfo = null;
          }
        }
      });
  }

  /**
   * Setup component state and initialization that needs to be restored when component is reused
   */
  private setupComponentState(): void {
    // Update language
    this.lang = this.languageService.currLang;

    // Detect Chromium version
    const raw = navigator.userAgent.match(/Chrom(?:e|ium)\/([0-9]+)\./);
    const chromiumVersion = raw ? parseInt(raw[1], 10) : null;
    const ua = navigator.userAgent;
    let isLG2017WebOS = false;
    if (ua.includes('Web0S')) {
      const chrome = ua.match(/Chrome\/([0-9]+)/);
      const chromeVersion = chrome ? parseInt(chrome[1], 10) : null;
      let webOSVersion = '';
      if (window.webOsVersion) {
        webOSVersion = window.webOsVersion;
      } else {
        webOSVersion = '';
      }
      isLG2017WebOS =
        (chromeVersion === 38 || chromeVersion === 39) &&
        webOSVersion.startsWith('3.');
    }
    this.isLegacyChromium =
      (chromiumVersion !== null && chromiumVersion <= 38) || isLG2017WebOS;
  }

  private sendNewRelicStart() {
    if (this.dataService.isAppStartSent) {
      return;
    }

    const elapsedSplash = this.sessionStorage.getItem(KEYS.ELAPSED_SPLASH);
    const timeToAppStart = this.sessionStorage.getItem(KEYS.TIME_TO_LOAD_APP);
    console.log('📊 Sending APP_START event storefront');
    this.newrelic.sendAppEvent(AppEventName.APP_START, {
      timeToAppStart: Number(timeToAppStart) - Number(elapsedSplash),
    });

    // Mark as sent
    this.dataService.isAppStartSent = true;
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.clearAutoScrollTimer();
    this.clearAutoScrollSubscription();
    this.fullReset();
  }

  /**
   * Called by reuse strategy before save the component
   */
  stop(): void {
    // this.dataService.horizontalIndex = this.navService.getHorizontalIndex();
    // this.dataService.verticalIndex = this.currentVerticalIndex;

    // Save scroll position to prevent unwanted scrolling on reload
    const carouselContainer = document.querySelector(
      '.storefront-carousel-container'
    ) as HTMLElement;
    if (carouselContainer) {
      this.dataService.storefrontScrollPosition = {
        scrollTop: carouselContainer.scrollTop,
        scrollLeft: carouselContainer.scrollLeft,
      };
    }
  }

  reload(storefrontData: any, tabId: string, fullReload: boolean) {
    this.resetDestroySubject();
    this.setupSubscriptions();
    this.setupAutoScroll();
    this.setupComponentState();
    const { containers, totalContainers, personalisationData } = storefrontData;
    const isHomeTab = this.dataService.homeTab === tabId;
    if (isHomeTab) {
      this.continueWatchingContainer =
        personalisationData?.resources?.length > 0 ? personalisationData : null;
    }

    if (fullReload) {
      this.reset();
      this.pageNumber = 1;
      this.maxContainers = 50;
      this.setCarousels(totalContainers, containers);
      this.navService.focusContainer('carousel-track', true, 0);
      this.dataService.horizontalIndex = null;
      this.dataService.verticalIndex = null;
      this.clearScrollPosition();
    } else {
      if (this.localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true') {
        this.isHeroFocused = true;
        this.dataService.emitVerticalMenuEvent({
          action: 'inactive',
        });
        this.clearScrollPosition();
      }
      if (this.isHeroFocused) {
        this.navService.focusContainer('carousel-track', true, 0);
        requestAnimationFrame(() => {
          this.currentResource =
            this.carouselResources[this.carouselCurrentIndex];
          const isPlayable = this.currentResource.ape
            ? this.currentResource.apUrl?.dash
            : null;
          if (!isPlayable) {
            this.simplePlayerService.resetEvent();
          }
          this.dataService.selectedCardData = this.currentResource;
        });
      }
      const hasContinueWatching = this.hasContinueWatching();
      if (
        this.authService.isLoggedIn() &&
        isHomeTab &&
        typeof this.continueWatchingIndex === 'number'
      ) {
        if (hasContinueWatching) {
          if (!this.continueWatchingContainer) {
            this.tempContainers.splice(this.continueWatchingIndex, 1);
            if (this.dataService.verticalIndex >= this.continueWatchingIndex) {
              this.dataService.verticalIndex =
                this.dataService.verticalIndex - 1;
            }
          } else {
            this.tempContainers.splice(
              this.continueWatchingIndex,
              1,
              this.continueWatchingContainer
            );
            this.continueWatchingResetId = new Date().getTime().toString();
            if (this.dataService.verticalIndex === this.continueWatchingIndex) {
              this.dataService.horizontalIndex = 0;
            }
          }
        } else {
          if (this.continueWatchingContainer) {
            this.tempContainers.splice(
              this.continueWatchingIndex,
              0,
              this.continueWatchingContainer
            );
            this.continueWatchingResetId = new Date().getTime().toString();
            if (this.dataService.verticalIndex > this.continueWatchingIndex) {
              this.dataService.verticalIndex = this.continueWatchingIndex + 1;
            }
          }
        }

        /* const hasContinueWatching = this.hasContinueWatching();
        if (!this.continueWatchingContainer && hasContinueWatching) {
          this.tempContainers.splice(this.continueWatchingIndex, 1);
          if (this.dataService.verticalIndex >= this.continueWatchingIndex) {
            this.dataService.verticalIndex = this.dataService.verticalIndex - 1;
          }
        } else if (this.continueWatchingContainer) {
          this.tempContainers.splice(
            this.continueWatchingIndex,
            hasContinueWatching ? 1 : 0,
            this.continueWatchingContainer
          );

          if (
            !hasContinueWatching &&
            this.dataService.verticalIndex >= this.continueWatchingIndex
          ) {
            this.dataService.verticalIndex = this.dataService.verticalIndex + 1;
          } else if (
            this.continueWatchingIndex === this.dataService.verticalIndex &&
            !this.dataService.forcePlayVideo &&
            hasContinueWatching
          ) {
            this.dataService.horizontalIndex = 0;
          }
          this.continueWatchingResetId = new Date().getTime().toString();
        } */
      }

      if (!this.isHeroFocused) {
        this.clearAutoScrollTimer();
        this.canFocus = true;
        setTimeout(() => {
          this.focusHorizontalCarousel(
            this.dataService.verticalIndex,
            this.dataService.horizontalIndex
          );
        }, 0);
      }

      requestAnimationFrame(() => {
        const carouselContainer = document.querySelector(
          '.storefront-carousel-container'
        ) as HTMLElement;
        if (carouselContainer && this.dataService.storefrontScrollPosition) {
          carouselContainer.scrollTop =
            this.dataService.storefrontScrollPosition.scrollTop;
        }
      });
    }
  }

  private clearScrollPosition(): void {
    this.dataService.storefrontScrollPosition = {
      scrollTop: 0,
      scrollLeft: 0,
    };
  }

  focusHorizontalCarousel(
    verticalIndex: number,
    horizontalIndex: number
  ): void {
    this.navService.setIndexContainer(
      `horizontal-carousel-cards-${verticalIndex}`,
      horizontalIndex
    );
    this.navService.focusContainer(
      `horizontal-carousel-cards-${verticalIndex}`,
      true,
      horizontalIndex
    );
  }

  async fetchStorefrontData(
    pageNumber: number,
    pageSize: number
  ): Promise<StorefrontVm> {
    if (!this.selectedTabId) {
      this.selectedTabId = this.tabService.getTabs()?.[0]?.id;
      this.tabService.currentTabId = this.selectedTabId;
      this.tabService.currentTabName = this.tabService.getTabs()?.[0]?.name;
    }
    let selectedTab: TabVm;
    try {
      this.loadingContainers = true;
      const catalogResponse = await this.catalogService.getStorefrontData(
        this.selectedTabId,
        pageSize,
        pageNumber
      );
      selectedTab = catalogResponse.tabVMs.find(
        (t) => t.id === this.selectedTabId
      );
      this.maxContainers = selectedTab.tabPagination.count;
    } catch (error) {
      console.warn(error);
      // this.nrPlaybackService.errorEvent(
      //   error,
      //   ErrorEvents.STOREFRONT_FETCH_ERROR
      // );
      throw ErrorFactory.get(error);
    }
    this.isLoading = false;
    return {
      containers: selectedTab.containers,
      totalContainers: selectedTab.tabPagination.count,
    };
  }

  async managePagination(): Promise<void> {
    if (
      this.pageNumber * this.pageSize < this.maxContainers &&
      !this.loadingContainers
    ) {
      this.pageNumber += 1;
      const { containers, totalContainers } = await this.fetchStorefrontData(
        this.pageNumber,
        this.pageSize
      );
      try {
        await this.setCarousels(totalContainers, containers);
      } catch (e) {
        console.warn(e);
      }
    }
  }

  onArrowUp(index: number): void {
    if (index !== 0) return;
    setTimeout(() => {
      this.currentResource = this.carouselResources[this.carouselCurrentIndex];
      this.heroFocused(true);
      this.dataService.selectedCardData = this.currentResource;
      this.navService.focusContainer('carousel-track', true, 0);
    }, 0);
  }

  heroFocused(isFocused: boolean): void {
    if (!isFocused) {
      this.clearAutoScrollTimer();
    } else {
      this.dataService.horizontalIndex = null;
      this.dataService.verticalIndex = null;
      this.clearScrollPosition();
    }
    this.isHeroFocused = isFocused;
    this.dataService.emitVerticalMenuEvent({
      action: isFocused ? 'inactive' : 'active',
    });
  }

  private async setCarousels(
    totalContainers: number,
    containers: ContainerVm[]
  ): Promise<void> {
    this.maxContainers = totalContainers;
    this.containers = containers;
    const filterCarouselContainer =
      containers !== undefined
        ? containers.find((container) => container?.layout === 'carousel')
        : undefined;

    if (filterCarouselContainer !== undefined) {
      // Duplicate cards once if less than 6, only at initial load
      const resources = filterCarouselContainer?.resources || [];
      resources.forEach(
        // added for static check
        (resource) => (resource.containerName = { en: 'hero' })
      );
      const len = resources.length;
      if (len > 0 && len < 6) {
        this.carouselResources = resources.concat(resources);
      } else {
        this.carouselResources = resources;
      }
      // this.currentResource = this.carouselContainer?.resources[0];
      this.dataService.selectedCardData = this.carouselResources[0];
      this.heroFocused(true);
    }

    for (let i = 0; i < Object.keys(containers)?.length; i++) {
      if (
        containers[i]?.name['en'].toUpperCase() === 'AHA ORIGINALS' ||
        containers[i]?.name['te'] === 'ఆహా ఒరిజినల్స్'
      ) {
        containers[i].layout = 'card';
      }
      if (!containers[i]?.resources) {
        const url = containers[i]?.contentUrl;
        const result = await this.storefrontHttpService
          .getDataFromContentURL(
            url,
            false,
            sessionStorage as SessionStorageService
          )
          .toPromise();
        containers[i].resources = result?.data;
      }

      const isBannerContainer = containers[i].bannerContainer;
      if (isBannerContainer) {
        const inAppBannerData = this.setInAppBannerData(isBannerContainer);
        if (inAppBannerData) {
          containers[i] = inAppBannerData;
        }
      }
    }

    let tempContainers = this.addViewAllResource(
      containers !== undefined
        ? containers.filter((container) => container?.layout === 'card')
        : undefined
    );
    tempContainers = this.cleanCarouselContainer(tempContainers);
    this.tempContainers.push(...tempContainers);
    tempContainers = null;

    // Update continue watching container after tempContainers is set
    this.findContinueWatchingIndex();
    if (
      this.tempContainers?.length > 0 &&
      this.continueWatchingContainer?.resources?.length > 0
    ) {
      this.updateContinueWatching();
    } else if (
      this.continueWatchingContainer?.resources?.length === 0 ||
      !this.continueWatchingContainer
    ) {
      this.tempContainers = this.tempContainers.filter(
        (container) => container.sourceType !== 'continue_watching'
      );
    }
    this.loadingContainers = false;

    // If no containers or only one container after initial load, trigger pagination
    // Use setTimeout for non-visual background task (better for low-end TVs)
    if (!this.tempContainers?.length || this.tempContainers.length === 1) {
      setTimeout(() => {
        this.managePagination();
      }, 0);
    }
    // this.ref.detectChanges();
  }

  private updateContinueWatching() {
    this.tempContainers.forEach((container) => {
      if (container.sourceType === 'continue_watching') {
        // Only update if container doesn't have resources or has empty resources
        if (container.resources?.length === 0) {
          container.resources = this.continueWatchingContainer.resources;
        }
      }
    });
  }
  private cwIndex(containers?: ContainerVm[]) {
    if (containers) {
      return containers.findIndex(
        (container) => container.sourceType === 'continue_watching'
      );
    }
    return this.tempContainers.findIndex(
      (container) => container.sourceType === 'continue_watching'
    );
  }

  private hasContinueWatching() {
    return this.cwIndex() > -1;
  }

  private findContinueWatchingIndex() {
    const index = this.cwIndex();
    if (index > -1) {
      this.continueWatchingIndex = index;
    }
  }

  private cleanCarouselContainer(tempContainers: ContainerVm[]): ContainerVm[] {
    return tempContainers.filter(
      (e) =>
        e?.resources?.length > 0 ||
        this.config.applicationConfig?.personalisedName
          ?.split(',')
          .includes(e?.sourceType)
    );
  }

  private addViewAllResource(containers: ContainerVm[]) {
    if (containers === undefined) {
      return undefined;
    }
    const newContainers = containers.map((container) => {
      return container;
    });

    return newContainers;
  }

  private reset(): void {
    this.carouselCurrentIndex = 0;
    this.containers = undefined;
    this.tempContainers = [];
    this.pageNumber = 1;
  }

  private fullReset(): void {
    this.clearAutoScrollTimer();
    this.clearAutoScrollSubscription();
    // Reset all stateful properties to their initial values
    this.carouselCurrentIndex = 0;
    this.scrollDirection = 'right';
    this.isAnimating = false;
    this.animationDuration = 300;
    this.selectedTabId = undefined;
    this.containers = undefined;
    this.isLoading = true;
    this.tempContainers = [];
    this.isHeroFocused = true;
    this.continueWatchingContainer = undefined;
    this.continueWatchingIndex = null;
    this.currentResource = undefined;
    this.contentDetailInfo = undefined;
    this.saved = false;
    this.continueWatchingResetId = null;
    this.isHeroImageLoaded = false;
    this.inAppBannerData = undefined;
    this.pageNumber = 1;
    this.maxContainers = 50;
    this.loadingContainers = true;
    this.autoScrollInterval = null;
    this.autoScrollSubscription = null;
    this.isComponentVisible = false;
    this.currentVerticalIndex = 0;
    this.carouselResources = undefined;
    this.isLegacyChromium = false;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    this.legacyCarouselTrack = null;
    this.legacyCarouselWrapper = null;
  }

  trackById(item: ResourceVm) {
    return item.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByTagKey(index: number, tag: any): string {
    return tag?.key || index;
  }

  onHeroKeyDown(event: KeyboardEvent) {
    setTimeout(() => {
      if (event.isDown()) {
        this.heroFocused(false);
        this.contentDetailInfo.customTags =
          this.contentDetailService.refreshCustomTags(
            this.currentResource,
            this.isHeroFocused
          );
        this.localStorage.setItem(LocalStorageKeys.TAB_CHANGED, 'false');
      } else if (event.isLeft()) {
        this.handleHorizontalScroll(true);
      } else if (event.isRight()) {
        this.handleHorizontalScroll(false);
      }
    }, 0);
  }

  isCardFocused(container: ContainerVm, index: number): void {
    this.isHeroFocused = false;

    this.dataService.emitVerticalMenuEvent({
      action: 'active',
    });

    const horizontalCarouselHeader = document.getElementById(container.id);
    if (horizontalCarouselHeader) {
      // Simple scroll without animations for better TV performance
      const container = document.querySelector(
        '.storefront-carousel-container'
      ) as HTMLElement;
      if (container) {
        const headerRect = horizontalCarouselHeader.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTop =
          headerRect.top - containerRect.top + container.scrollTop;
        container.scrollTop = scrollTop;
      }
    }
    this.navService.setVerticalIndex(index);
    this.currentVerticalIndex = index;
  }

  onFocusProcessed(): void {
    this.canFocus = false;
  }

  onClick() {
    if (!this.isHeroFocused) return;
    this.clearAutoScrollTimer();
    const resource = this.currentResource;
    this.dataService.selectedCardData = resource;
    const { type, id: contentID } = resource;
    if (directToPlayer(resource)) {
      this.router.navigate([`player/${type}/${contentID}`], {
        state: { type, id: contentID },
      });
    } else {
      this.router.navigate([`/${resource.contentType}/${resource.uniqueId}`]);
    }
  }

  private setupAutoScroll() {
    this.clearAutoScrollTimer();
    this.clearAutoScrollSubscription();

    this.autoScrollSubscription = this.simplePlayerService.playerEvents$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (event) =>
            event.tabId === this.selectedTabId &&
            [
              PlayerEventType.VIDEO_AVAILABLE,
              PlayerEventType.VIDEO_COMPLETED,
              PlayerEventType.FORCE_DESTROY,
            ].includes(event.type)
        )
      )
      .subscribe((event) => {
        switch (event.type) {
          case PlayerEventType.VIDEO_AVAILABLE:
            this.clearAutoScrollTimer();
            if (
              this.isComponentVisible &&
              event.data === false &&
              this.isHeroFocused
            ) {
              this.autoScrollInterval = setInterval(() => {
                if (this.isComponentVisible && this.isHeroFocused) {
                  this.autoScroll();
                }
              }, 5000);
            }
            break;
          case PlayerEventType.VIDEO_COMPLETED:
            this.clearAutoScrollTimer();
            if (this.isComponentVisible && this.isHeroFocused) {
              this.autoScroll();
            }
            break;
          case PlayerEventType.FORCE_DESTROY:
            this.clearAutoScrollTimer();
            break;
        }
      });
  }

  private autoScroll() {
    // Validate state before proceeding
    if (!this.isComponentVisible || !this.isHeroFocused) {
      return;
    }

    this.clearAutoScrollTimer();
    if (this.isLegacyChromium) {
      this.scrollLegacyCarouselToActive();
    } else {
      this.handleHorizontalScroll();
    }
  }

  private clearAutoScrollTimer() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  private clearAutoScrollSubscription() {
    if (this.autoScrollSubscription) {
      this.autoScrollSubscription.unsubscribe();
      this.autoScrollSubscription = null;
    }
  }

  onImageLoad(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement.complete) {
      this.isHeroImageLoaded = true;
    }
  }

  openVerticalMenu() {
    this.dataService.emitVerticalMenuEvent({
      action: 'open',
    });
  }

  // Banner logic
  setInAppBannerData(container: BannerContainerVm) {
    if (this.inAppBannerData) {
      return null;
    }
    const bannerContainer: ContainerVm = {
      id: IN_APP_BANNER_ID,
      name: container.name,
      layout: 'card',
      resources: [],
    };
    try {
      const SUBSCRIBE_NOW_TITLE_MAP = {
        en: 'Subscribe Now',
        ta: 'இப்போது குழுசேரவும்',
        te: 'ఇప్పుడే సబ్స్క్రయిబ్ చేయండి',
      };

      const UPGRADE_NOW_TITLE_MAP = {
        en: 'Upgrade Now',
        ta: 'இப்போது மேம்படுத்தவும்',
        te: 'ఇప్పుడు అప్గ్రేడ్ చేయండి',
      };

      const RENEW_NOW_TITLE_MAP = {
        en: 'Renew Now',
        ta: 'இப்போது புதுப்பிக்கவும்', // Correct Tamil for Renew Now
        te: 'ఇప్పుడు పునరుద్ధరించండి', // Correct Telugu for Renew Now
      };
      const userType = this.userEntitlementService.bannerSubscriptionStatus();
      const banner = container.banners.find((b) => b.type === userType);
      if (!banner) {
        return null;
      }
      // Optimized: Switch statement (more readable)
      let title: typeof SUBSCRIBE_NOW_TITLE_MAP | null = null;
      switch (userType) {
        case UserSubscriptionStatus.ANONYMOUS:
        case UserSubscriptionStatus.REGISTERED:
          title = SUBSCRIBE_NOW_TITLE_MAP;
          break;
        case UserSubscriptionStatus.SUBSCRIBED:
          title = UPGRADE_NOW_TITLE_MAP;
          break;
        case UserSubscriptionStatus.CHURNED:
          title = RENEW_NOW_TITLE_MAP;
          break;
      }
      bannerContainer.resources = this.inAppBannerAdapter(
        container,
        banner,
        title
      );
      this.inAppBannerData = bannerContainer;
      return bannerContainer;
    } catch (error) {
      console.error('Error in setInAppBannerData:', error);
    }
    return null;
  }

  getImageUrl(value: string): string {
    return `${value}?width=2560`;
  }

  private inAppBannerAdapter(
    container: BannerContainerVm,
    banner: BannerVm,
    title: any
  ): ResourceVm[] | null {
    try {
      const bannerImage = this.getImageUrl(banner.bannerImage);
      const providerImage = this.getImageUrl(banner.providerImage);

      return [
        {
          id: IN_APP_BANNER_ID,
          key: '',
          name: container.name.en,
          type: '',
          title,
          contentType: '',
          trailers: [],
          keywords: [],
          bannerImage,
          apUrl: null,
          ape: false,
          shortDescription: null,
          providerImage,
        },
      ];
    } catch (error) {
      console.error('Error in bannerAdapter:', error);
      return null;
    }
  }

  trackByContainerId(container: any): any {
    return container.id;
  }

  handleHorizontalScroll(invert: boolean = false): void {
    if (this.isAnimating) return;
    // Double-check focus state to ensure carousel never moves when focus is out
    if (!this.isHeroFocused || !this.carouselResources?.length) return;

    const resources = this.carouselResources;
    const total = resources.length;

    this.scrollDirection = invert ? 'left' : 'right';
    this.isAnimating = true;

    if (!invert) {
      // Move right
      this.carouselCurrentIndex = (this.carouselCurrentIndex + 1) % total;
    } else {
      // Move left
      this.carouselCurrentIndex =
        (this.carouselCurrentIndex - 1 + total) % total;
    }

    this.scrollTimeout = setTimeout(() => {
      this.isAnimating = false;
      this.scrollTimeout = null;
    }, this.animationDuration);

    // this.currentResource = resources[this.carouselCurrentIndex];
    this.dataService.selectedCardData = resources[this.carouselCurrentIndex];
    this.navService.setIndexContainer('container-all', 0);
  }

  onStorefrontCarouselKeydown(event: KeyboardEvent): void {
    if (event.isDown()) {
      this.managePagination();
    }
    return;
  }

  private scrollLegacyCarouselToActive() {
    this.carouselCurrentIndex =
      (this.carouselCurrentIndex + 1) % this.carouselResources.length;
    if (
      this.legacyCarouselTrack &&
      this.legacyCarouselWrapper &&
      this.carouselCurrentIndex != null
    ) {
      const track: HTMLElement = this.legacyCarouselTrack.nativeElement;
      const wrapper: HTMLElement = this.legacyCarouselWrapper.nativeElement;
      const cards = track.querySelectorAll('.carousel-item');
      const activeCard = cards[this.carouselCurrentIndex] as HTMLElement;
      if (activeCard) {
        const wrapperWidth = wrapper.offsetWidth;
        const cardLeft = activeCard.offsetLeft;
        const cardWidth = activeCard.offsetWidth;
        const scrollTo = cardLeft - wrapperWidth / 2 + cardWidth / 2;
        wrapper.scrollLeft = scrollTo;
      }
    }
  }
}
